import { useEffect, useState } from "react";
import api from "../../lib/api";
import { CalendarBlank, Storefront, Users, CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Admin tile — Weekly Coverage
 *
 * Shows how much of the owner base has actually uploaded events for the
 * SELECTED calendar week. Three KPIs (owners covered, trucks covered, total
 * events) plus a 7-bar daily distribution. Prev/Next navigation lets admins
 * peek ahead at next-week prep without leaving the page.
 */
export default function ThisWeekEventsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0); // 0 = this week, 1 = next, -1 = prev

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/admin/this-week-events", { params: { week_offset: offset } })
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [offset]);

  const ownersTotal = data?.owners_total || 0;
  const ownersWith = data?.owners_with_events || 0;
  const trucksTotal = data?.trucks_total || 0;
  const trucksWith = data?.trucks_with_events || 0;
  const totalEvents = data?.total_events || 0;
  const perDay = data?.per_day || [];
  const maxCount = Math.max(1, ...perDay.map((d) => d.count));
  const ownerPct = ownersTotal ? Math.round((ownersWith / ownersTotal) * 100) : 0;

  const tone =
    ownerPct >= 80
      ? { label: "Strong week", color: "text-emerald-600 dark:text-emerald-400" }
      : ownerPct >= 50
      ? { label: "On track", color: "text-foreground" }
      : ownerPct >= 25
      ? { label: "Patchy", color: "text-amber-600 dark:text-amber-400" }
      : { label: "Quiet week", color: "text-muted-foreground" };

  const headingLabel =
    offset === 0 ? "This week's coverage"
    : offset === 1 ? "Next week's coverage"
    : offset === -1 ? "Last week's coverage"
    : `${offset > 0 ? "+" : ""}${offset} week coverage`;
  const descCopy =
    offset === 0
      ? "How many real trucks have uploaded events this calendar week."
      : offset > 0
      ? "Who's already prepped events for this upcoming week."
      : "How many trucks posted events that week.";

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="admin-this-week-events"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <CalendarBlank weight="duotone" className="h-4 w-4 text-primary" />
            {headingLabel}
          </div>
          <h3 className="font-display font-semibold text-lg sm:text-xl mt-1 leading-tight">
            {data?.week_label || "—"}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {descCopy}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-none">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(-4, o - 1))}
            disabled={loading || offset <= -4}
            className="h-8 w-8 grid place-items-center rounded-full border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous week"
            data-testid="admin-tw-prev-week"
          >
            <CaretLeft weight="bold" className="h-3.5 w-3.5" />
          </button>
          {offset !== 0 && (
            <button
              type="button"
              onClick={() => setOffset(0)}
              disabled={loading}
              className="h-8 px-3 text-[11px] font-semibold uppercase tracking-wider rounded-full bg-primary/10 text-primary hover:bg-primary/20"
              data-testid="admin-tw-this-week"
            >
              This week
            </button>
          )}
          <button
            type="button"
            onClick={() => setOffset((o) => Math.min(4, o + 1))}
            disabled={loading || offset >= 4}
            className="h-8 w-8 grid place-items-center rounded-full border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next week"
            data-testid="admin-tw-next-week"
          >
            <CaretRight weight="bold" className="h-3.5 w-3.5" />
          </button>
          {!loading && (
            <span className={`ml-2 text-xs font-semibold uppercase tracking-wider ${tone.color}`}>
              {tone.label}
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi
          icon={Users}
          label="Owners with events"
          value={`${ownersWith} / ${ownersTotal}`}
          subtext={ownersTotal ? `${ownerPct}% of all owners` : "—"}
          testId="admin-tw-owners"
        />
        <Kpi
          icon={Storefront}
          label="Trucks with events"
          value={`${trucksWith} / ${trucksTotal}`}
          subtext={
            trucksTotal
              ? `${Math.round((trucksWith / trucksTotal) * 100)}% of all trucks`
              : "—"
          }
          testId="admin-tw-trucks"
        />
        <Kpi
          icon={CalendarBlank}
          label="Total events"
          value={String(totalEvents)}
          subtext={
            ownersWith
              ? `${(totalEvents / ownersWith).toFixed(1)} per active owner`
              : "—"
          }
          testId="admin-tw-events"
        />
      </div>

      {/* 7-bar daily breakdown */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">
          Events per day
        </div>
        <div className="flex items-end gap-2 h-28" data-testid="admin-tw-daily-chart">
          {perDay.map((d) => {
            const heightPct = (d.count / maxCount) * 100;
            return (
              <div
                key={d.day}
                className="flex-1 flex flex-col items-center gap-1 min-w-0"
                data-testid={`admin-tw-bar-${d.day}`}
              >
                <div className="text-[11px] font-semibold tabular-nums text-foreground">
                  {d.count}
                </div>
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={`w-full rounded-t-md transition-all ${
                      d.count > 0
                        ? "bg-gradient-to-t from-primary to-orange-400"
                        : "bg-muted"
                    }`}
                    style={{ height: `${Math.max(heightPct, d.count > 0 ? 6 : 2)}%` }}
                    title={`${d.label}: ${d.count} event${d.count === 1 ? "" : "s"}`}
                  />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {d.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, subtext, testId }) {
  return (
    <div
      className="rounded-xl border border-border bg-background/40 p-3"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon weight="duotone" className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 font-display font-semibold text-xl sm:text-2xl tabular-nums leading-tight">
        {value}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{subtext}</div>
    </div>
  );
}
